<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <b-container fluid>
      <b-row align-h="between" align-v="center">
        <b-col cols="12" md="5" lg="4" class="p-3 mb-0">
          <b-form-input
            v-model="search"
            placeholder="Search name, trade or email..."
            debounce="200"
          />
        </b-col>
      </b-row>

      <div
        v-if="!isLoading && !tradies.length && !search"
        class="text-center text-muted py-4"
      >
        No tradies are ending their company association.
      </div>
      <div
        v-else-if="!isLoading && !tradies.length"
        class="text-center text-muted py-4"
      >
        No matches for "{{ search }}".
      </div>

      <template v-else>
        <!-- Mobile: compact list rows (hidden on md+) -->
        <div class="d-md-none pb-2">
          <div
            v-for="item in tradies"
            :key="item.id"
            class="mobile-leaving-item"
            :class="rowClass(item)"
            @click="viewTradie(item)"
          >
            <div class="d-flex align-items-center">
              <b-avatar
                :src="avatarUrl(item)"
                :text="initials(item.full_name)"
                size="32px"
                variant="primary"
                class="mr-2 flex-shrink-0"
              />
              <div class="flex-grow-1" style="min-width: 0">
                <div class="d-flex align-items-center justify-content-between">
                  <span class="mobile-leaving__name">{{
                    item.full_name || '-'
                  }}</span>
                  <span
                    class="mobile-leaving__countdown ml-2 flex-shrink-0"
                    :class="urgencyClass(item.leave_at)"
                  >
                    <b-icon icon="exclamation-triangle-fill" class="mr-1" />
                    {{ countdownLabel(item.leave_at) }}
                  </span>
                </div>
                <div class="mobile-leaving__meta">
                  {{ item.email || '-'
                  }}<template v-if="item.trade"> · {{ item.trade }}</template>
                </div>
                <div class="d-flex align-items-center mt-1">
                  <b-badge
                    v-if="isAccountDeletion(item)"
                    variant="danger"
                    pill
                    class="mr-2"
                    >Deleting account</b-badge
                  >
                  <b-button
                    v-if="canEndNow"
                    size="sm"
                    variant="danger"
                    class="end-now-btn"
                    :disabled="endingId === item.id"
                    @click.stop="endNow(item)"
                  >
                    End now
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop: full table (hidden below md) -->
        <div class="d-none d-md-block">
          <b-table
            id="leaving-tradies-table"
            class="mt-4"
            responsive
            hover
            :fields="fields"
            :items="tradies"
            :tbody-tr-class="rowClass"
            @row-clicked="viewTradie"
          >
            <template #cell(full_name)="data">
              <div class="d-flex align-items-center">
                <b-avatar
                  :src="avatarUrl(data.item)"
                  :text="initials(data.item.full_name)"
                  size="24px"
                  variant="primary"
                  class="mr-2"
                />
                <span>{{ data.item.full_name || '-' }}</span>
                <b-badge
                  v-if="isAccountDeletion(data.item)"
                  variant="danger"
                  pill
                  class="ml-2"
                  >Deleting account</b-badge
                >
              </div>
            </template>

            <template #cell(trade)="data">
              {{ data.item.trade || '-' }}
            </template>

            <template #cell(email)="data">
              {{ data.item.email || '-' }}
            </template>

            <template #cell(phone_number)="data">
              {{ data.item.phone_number || '-' }}
            </template>

            <template #cell(leave_at)="data">
              <span :class="urgencyClass(data.item.leave_at)">
                <b-icon
                  icon="exclamation-triangle-fill"
                  class="mr-1"
                  :class="urgencyClass(data.item.leave_at)"
                />
                {{ countdownLabel(data.item.leave_at) }}
              </span>
              <span class="text-muted">
                · {{ formatLeaveAt(data.item.leave_at) }}
              </span>
            </template>
            <template #cell(actions)="data">
              <b-button
                v-if="canEndNow"
                size="sm"
                variant="danger"
                class="end-now-btn"
                :disabled="endingId === data.item.id"
                @click.stop="endNow(data.item)"
              >
                End now
              </b-button>
            </template>
          </b-table>
        </div>
      </template>

      <b-pagination
        v-if="total > perPage"
        v-model="currentPage"
        :total-rows="total"
        :per-page="perPage"
        aria-controls="leaving-tradies-table"
        align="center"
      />

      <p v-if="tradies.length" align="center">
        Showing {{ tradies.length }} out of total of {{ total }} tradies
      </p>
    </b-container>
  </b-overlay>
</template>

<script>
import { mapGetters } from 'vuex'
import { parseServerDate } from '@/utils/dateHelper'

const HOUR_MS = 60 * 60 * 1000

export default {
  data() {
    return {
      isLoading: false,
      tradies: [],
      total: 0,
      currentPage: 1,
      perPage: 10,
      search: '',
      now: Date.now(),
      tickHandle: null,
      fields: [
        { key: 'full_name', label: 'Name' },
        { key: 'trade', label: 'Trade' },
        { key: 'email', label: 'Email' },
        { key: 'phone_number', label: 'Phone' },
        { key: 'leave_at', label: 'Ending In', class: 'text-right' },
        { key: 'actions', label: '', class: 'text-right' },
      ],
      endingId: null,
    }
  },

  computed: {
    ...mapGetters('auth', ['hasPermission']),

    canEndNow() {
      return this.hasPermission('tradies.remove')
    },
  },

  watch: {
    currentPage() {
      this.fetchTradies()
    },

    search() {
      // A new search term invalidates whatever page we were on. Only fetch
      // here if we're already on page 1 — otherwise resetting currentPage
      // triggers the watcher above, which would double up the request.
      if (this.currentPage === 1) {
        this.fetchTradies()
      } else {
        this.currentPage = 1
      }
    },
  },

  methods: {
    async fetchTradies() {
      this.isLoading = true
      try {
        const { data } = await this.$api.get('companies/tradies', {
          params: {
            leaving: true,
            limit: this.perPage,
            page: this.currentPage,
            search: this.search || undefined,
          },
        })
        const items = (data && data.data) || []
        this.tradies = items.filter((t) => t.leave_at)
        this.total = (data && data.meta && data.meta.total) || 0
      } catch (err) {
        this.tradies = []
        this.total = 0
      } finally {
        this.isLoading = false
        this.$emit('count-changed', this.total)
      }
    },

    avatarUrl(item) {
      if (!item || !item.avatar) return ''
      if (item.avatar.url) return item.avatar.url
      if (item.avatar.data && item.avatar.data.url) return item.avatar.data.url
      return ''
    },

    initials(name) {
      if (!name) return '?'
      return name
        .split(/\s+/)
        .map((w) => w.charAt(0))
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },

    hoursUntil(iso) {
      if (!iso) return null
      const target = parseServerDate(iso)
      if (!target) return null
      return (target.getTime() - this.now) / HOUR_MS
    },

    urgencyClass(iso) {
      const hours = this.hoursUntil(iso)
      if (hours == null) return ''
      if (hours <= 24) return 'text-danger font-weight-bold'
      if (hours <= 72) return 'text-warning font-weight-bold'
      return 'text-muted'
    },

    countdownLabel(iso) {
      const hours = this.hoursUntil(iso)
      if (hours == null) return '-'
      if (hours <= 0) return 'Ending now'
      if (hours < 1) {
        const mins = Math.max(1, Math.round(hours * 60))
        return `In ${mins} min${mins === 1 ? '' : 's'}`
      }
      if (hours < 24) {
        const h = Math.max(1, Math.round(hours))
        return `In ${h} hour${h === 1 ? '' : 's'}`
      }
      const d = Math.round(hours / 24)
      return `In ${d} day${d === 1 ? '' : 's'}`
    },

    formatLeaveAt(iso) {
      if (!iso) return ''
      try {
        const d = parseServerDate(iso)
        if (!d) return iso
        return d.toLocaleString('en-AU', {
          day: 'numeric',
          month: 'short',
          hour: 'numeric',
          minute: '2-digit',
        })
      } catch (e) {
        return iso
      }
    },

    rowClass(item) {
      if (!item) return ''
      const hours = this.hoursUntil(item.leave_at)
      if (hours == null) return ''
      if (hours <= 24) return 'cursor-pointer row-urgent'
      if (hours <= 72) return 'cursor-pointer row-warning'
      return 'cursor-pointer'
    },

    isAccountDeletion(item) {
      return !!item && item.leave_reason === 'account_deletion'
    },

    // The countdown is a courtesy window, not a lock: the company can end the
    // association straight away (same action as removing the tradie).
    async endNow(item) {
      if (!item || !this.canEndNow) return
      const ok = await this.$bvModal.msgBoxConfirm(
        `End the association with ${
          item.full_name || 'this tradie'
        } now? They will be removed from your company and its projects immediately.`,
        {
          title: 'End association now?',
          okTitle: 'End now',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return

      this.endingId = item.id
      try {
        await this.$api.post('companies/remove-tradie', {
          tradie_id: item.id,
        })
        this.$bvToast.toast(
          `Association with ${item.full_name || 'tradie'} ended.`,
          {
            title: 'Success',
            autoHideDelay: 4000,
            variant: 'success',
          },
        )
        await this.fetchTradies()
      } catch (err) {
        this.$bvToast.toast(
          'Unable to end the association. Please try again.',
          {
            title: 'Error',
            autoHideDelay: 4000,
            variant: 'danger',
          },
        )
      } finally {
        this.endingId = null
      }
    },

    viewTradie(item) {
      if (!item || !item.id) return
      this.$router.push({
        name: 'tradies.view-tradie',
        params: { tradieId: item.id },
      })
    },
  },

  mounted() {
    this.fetchTradies()
    this.tickHandle = window.setInterval(() => {
      this.now = Date.now()
    }, 60 * 1000)
  },

  beforeDestroy() {
    if (this.tickHandle) window.clearInterval(this.tickHandle)
  },
}
</script>

<style lang="scss" scoped>
/deep/ .row-urgent {
  background-color: #fff5f5;
}
/deep/ .row-warning {
  background-color: #fff8e6;
}
/deep/ .cursor-pointer {
  cursor: pointer;
}
.mobile-leaving-item {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
  cursor: pointer;
}

.mobile-leaving__name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #364052;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-leaving__countdown {
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.mobile-leaving__meta {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.15rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
